/**
 * The success cases the AI found, to review before saving: each a card (label, client, metrics count, the AI marker
 * with its quote) with **Review** (the case editor's fields in a dialog) and **Leave out**. Every case is saved
 * internal; making one shareable is a separate, confirmed step on the saved asset.
 */

import { useState } from 'react';

import { Modal } from '@/ui/Modal';

import { type CaseForm, caseErrors } from '../cases/caseForm';
import { IdentityFields, TextFields, type SetField } from '../cases/CaseFields';
import { MetricsEditor } from '../cases/MetricsEditor';
import type { DraftCase } from './fromFilesDraft';
import { SourceNote, type FileNames } from '../files/SourceNote';

function CaseDialog({ draft, onDone, onClose }: { draft: DraftCase; onDone: (form: CaseForm) => void; onClose: () => void }) {
  const [form, setForm] = useState<CaseForm>(draft.form);
  const set: SetField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const errors = caseErrors(form);
  const { anonymized_label: problem, ...fieldProblems } = errors;
  return (
    <Modal
      title="Review success case"
      subtitle="Saved internal with the asset. The client's name and aliases are internal; clients read the anonymized label."
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={Object.keys(errors).length > 0} onClick={() => onDone(form)}>Done</button>
        </>
      }
    >
      <div className="stack-md">
        {problem && <p className="hint" aria-live="polite">{problem}</p>}
        <IdentityFields form={form} set={set} errors={fieldProblems} />
        <TextFields form={form} set={set} errors={fieldProblems} />
        <MetricsEditor rows={form.metrics} onChange={(rows) => set('metrics', rows)} errors={fieldProblems} />
      </div>
    </Modal>
  );
}

export function ReviewCases({
  cases,
  names,
  onChange,
  onRemove,
}: {
  cases: DraftCase[];
  names: FileNames;
  onChange: (key: string, form: CaseForm) => void;
  onRemove: (key: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const editing = cases.find((c) => c.key === open);
  return (
    <section className="stack-md" aria-labelledby="review-cases-title">
      <h2 id="review-cases-title">Success cases ({cases.length})</h2>
      {cases.length === 0 && <p className="hint">The AI found no success case the files could back.</p>}
      {cases.map((c, i) => (
        <article key={c.key} className="card" aria-labelledby={`${c.key}-title`}>
          <div className="card-body stack-sm">
            <h3 id={`${c.key}-title`}>{c.form.anonymized_label || `Success case ${i + 1}`}</h3>
            <p className="hint">
              {c.form.client_name ? `Client: ${c.form.client_name} (internal)` : 'The files do not name the client.'} ·{' '}
              {c.form.metrics.length === 1 ? '1 metric' : `${c.form.metrics.length} metrics`}
            </p>
            {c.source && <SourceNote source={c.source} names={names} what={`Success case ${i + 1}`} />}
            <div className="form-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => onRemove(c.key)}>Leave out case {i + 1}</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(c.key)}>Review case {i + 1}</button>
            </div>
          </div>
        </article>
      ))}
      {editing && (
        <CaseDialog
          draft={editing}
          onClose={() => setOpen(null)}
          onDone={(form) => {
            onChange(editing.key, form);
            setOpen(null);
          }}
        />
      )}
    </section>
  );
}
